import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { IMAGES, REGIONS, INITIATIVES, STEPS, CHECKLIST, WHY, PROVIDE_LIST } from "./data";
import { EnquiryForm } from "./EnquiryForm";

export const GlobalMap = () => (
  <section data-testid="global-sourcing-section" className="relative py-24 md:py-32 bg-[#0A1F16] grain-overlay overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.worldNight} alt="" className="w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-[#0A1F16]/60" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-global-sourcing-network">
          Global Sourcing Network
        </p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#FBF9F6] max-w-3xl">
          Source Globally. Select Strategically.
        </h2>
        <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
        <div className="mt-8 space-y-5 text-base md:text-lg font-light text-[#FBF9F6]/75 max-w-3xl leading-relaxed" data-testid="global-sourcing-intro">
          <p>
            RSC Imports identifies and develops relationships with producers across established and
            emerging sourcing markets worldwide.
          </p>
          <p>
            Our sourcing decisions are not restricted by geography. We focus on producers capable of
            delivering the right combination of quality, commercial viability, manufacturing
            capability, documentation, consistency and long-term supply.
          </p>
        </div>
      </Reveal>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-[#FBF9F6]/10" data-testid="sourcing-regions-grid">
        {REGIONS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.08} className="border-b border-r border-[#FBF9F6]/10">
            <div className="p-6 md:p-8 h-full" data-testid={`region-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="block h-1 w-6 bg-[#BFA26F] mb-5" />
              <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-[#FBF9F6] mb-2">{r.name}</h3>
              <p className="text-xs font-light text-[#FBF9F6]/55 leading-relaxed">{r.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-6 text-xs font-light tracking-wide text-[#FBF9F6]/45 italic" data-testid="sourcing-regions-note">
          Regions shown represent international sourcing markets and supplier networks.
        </p>
      </Reveal>
    </div>
  </section>
);

export const Initiatives = () => (
  <section data-testid="initiatives-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-sourcing-initiatives">
          Current Sourcing Initiatives
        </p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl">
          Expanding Our International Supplier Network.
        </h2>
        <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
        <p className="mt-8 mb-16 text-base md:text-lg font-light text-[#4A5D54] max-w-2xl leading-relaxed" data-testid="initiatives-intro">
          As consumer preferences evolve across our destination markets, RSC Imports continuously
          develops new supplier relationships and explores emerging product categories around the
          world.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="initiatives-grid">
        {INITIATIVES.map((it, i) => (
          <Reveal key={it.tag} delay={i * 0.12}>
            <div className="h-full border border-[#0D2B1E]/10 border-t-2 border-t-[#BFA26F] bg-white hover:-translate-y-0.5 transition-transform duration-300 group overflow-hidden" data-testid={`initiative-card-${i + 1}`}>
              <div className="h-44 md:h-48 overflow-hidden">
                <img src={it.image} alt={it.tag} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.2em] text-[#BFA26F] font-semibold mb-5">{it.tag}</p>
                <h3 className="font-display text-xl md:text-2xl font-semibold text-[#111413] mb-4 leading-snug">
                  {it.title}
                </h3>
                <p className="text-sm font-light text-[#4A5D54] leading-relaxed">{it.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ProcessTimeline = ({ overline, title, intro, showHeader = true }) => (
  <section data-testid="process-section" className={showHeader ? "py-24 md:py-32 bg-white" : "py-20 md:py-28"}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      {showHeader && (
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-procurement-model">
            {overline}
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl">
            {title}
          </h2>
          <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
          <p className="mt-8 mb-16 text-base md:text-lg font-light text-[#4A5D54] max-w-2xl leading-relaxed" data-testid="process-intro">
            {intro}
          </p>
        </Reveal>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="relative lg:col-span-7" data-testid="process-timeline">
          <div className="absolute left-[27px] md:left-[39px] top-2 bottom-2 w-px bg-[#0D2B1E]/15" />
          <div className="space-y-10 md:space-y-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div className="relative flex gap-6 md:gap-10 items-start group" data-testid={`process-step-${i + 1}`}>
                <div className="relative z-10 shrink-0 w-14 h-14 md:w-20 md:h-20 bg-white border border-[#0D2B1E]/15 group-hover:border-[#BFA26F] flex items-center justify-center transition-colors duration-300">
                  <span className="font-display text-2xl md:text-4xl font-medium text-[#BFA26F]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="pt-1 md:pt-4 pb-2 border-b border-[#0D2B1E]/10 flex-1">
                  <h3 className="text-sm md:text-base font-bold tracking-[0.15em] uppercase text-[#111413] mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm md:text-base font-light text-[#4A5D54] leading-relaxed max-w-2xl">
                    {s.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-5">
          <Reveal delay={0.2}>
            <div className="sticky top-32" data-testid="process-side-image">
              <img src={IMAGES.logistics} alt="International distribution infrastructure" className="w-full h-[520px] object-cover border border-[#0D2B1E]/10" />
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#4A5D54]/70">
                Structured Procurement. Disciplined Execution.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const chain = ["Producer", "RSC Imports", "Import / Distribution", "Channel Partner", "Consumer"];

export const Philosophy = () => (
  <section data-testid="philosophy-section" className="relative py-24 md:py-32 bg-[#0A1F16] grain-overlay">
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-procurement-philosophy">
          Our Procurement Philosophy
        </p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#FBF9F6] max-w-3xl">
          Global Products. Local Market Intelligence.
        </h2>
        <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
        <p className="mt-8 text-base md:text-lg font-light text-[#FBF9F6]/75 leading-relaxed max-w-3xl" data-testid="philosophy-body">
          Successful international distribution requires more than moving products between
          countries. Consumer behaviour, regulations, pricing structures, packaging expectations,
          retail environments and distribution channels differ significantly between markets. RSC
          Imports therefore assesses every sourcing opportunity from the perspective of the
          destination market.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-12 flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-y-4" data-testid="philosophy-chain">
          {chain.map((c, i) => (
            <div key={c} className="flex items-center">
              <span
                className={`px-4 py-2.5 md:px-6 md:py-3 text-xs md:text-sm font-semibold tracking-wider uppercase border ${
                  i === 1
                    ? "bg-[#BFA26F] text-[#0A1F16] border-[#BFA26F]"
                    : "text-[#FBF9F6]/85 border-[#FBF9F6]/25"
                }`}
              >
                {c}
              </span>
              {i < chain.length - 1 && (
                <ArrowRight size={16} strokeWidth={1.5} className="mx-2 md:mx-3 text-[#BFA26F] rotate-90 sm:rotate-0" />
              )}
            </div>
          ))}
        </div>
        <p className="mt-10 text-sm md:text-base font-light text-[#FBF9F6]/65 italic max-w-3xl leading-relaxed">
          This market-led approach allows us to concentrate resources on products with credible
          long-term commercial potential rather than purely transactional trading opportunities.
        </p>
      </Reveal>
    </div>
  </section>
);

export const SupplierChecklist = ({ showHeader = true }) => (
  <section data-testid="supplier-requirements-section" className={showHeader ? "py-24 md:py-32" : "py-8 md:py-12"}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      {showHeader && (
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-supplier-partnerships">
            Supplier Partnerships
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl">
            Long-Term Partners. Not One-Off Transactions.
          </h2>
          <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
          <p className="mt-8 mb-14 text-base md:text-lg font-light text-[#4A5D54] max-w-2xl leading-relaxed" data-testid="supplier-checklist-intro">
            RSC Imports seeks to establish sustainable relationships with manufacturers and
            producers capable of supporting international growth.
          </p>
        </Reveal>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0D2B1E]/10" data-testid="supplier-checklist-grid">
        {CHECKLIST.map((c, i) => (
          <Reveal key={c} delay={i * 0.03} className="border-b border-r border-[#0D2B1E]/10">
            <div className="p-5 md:p-6 flex items-start gap-4 bg-white/60 h-full">
              <Check size={18} strokeWidth={1.5} className="text-[#BFA26F] mt-1 shrink-0" />
              <p className="text-sm font-medium tracking-wide text-[#111413] uppercase">{c}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-8 text-sm md:text-base font-light text-[#4A5D54] italic leading-relaxed max-w-3xl" data-testid="supplier-sustainability-note">
          Sustainability, traceability and responsible sourcing are increasingly important
          considerations across our international procurement activities.
        </p>
      </Reveal>
    </div>
  </section>
);

export const WhyRsc = () => (
  <section data-testid="why-rsc-section" className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-why-rsc">
          Why RSC Imports
        </p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl mb-16">
          A Multi-Market Approach to International Procurement.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-[#0D2B1E]/10" data-testid="why-rsc-grid">
        {WHY.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.08} className="border-b border-r border-[#0D2B1E]/10">
            <div className="p-6 md:p-8 h-full bg-[#FBF9F6]/60 hover:bg-[#FBF9F6] hover:-translate-y-0.5 transition-[background-color,transform] duration-300" data-testid={`why-card-${i + 1}`}>
              <p className="font-display text-3xl font-medium text-[#BFA26F]/70 mb-6">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-[#111413] mb-4 leading-snug">
                {r.title}
              </h3>
              <p className="text-sm font-light text-[#4A5D54] leading-relaxed">{r.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const PartnerSection = () => (
  <section id="partner-form" data-testid="partner-section" className="relative py-24 md:py-32 bg-[#0A1F16] grain-overlay scroll-mt-20">
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-partner">
            Partner With RSC Imports
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#FBF9F6]">
            For International Producers.
          </h2>
          <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
          <div className="mt-8 space-y-4 text-base md:text-lg font-light text-[#FBF9F6]/75 leading-relaxed" data-testid="partner-intro">
            <p>
              If you manufacture an exceptional product and are seeking expansion opportunities
              across the Middle East, India or Southeast Asia, our international sourcing team would
              like to hear from you.
            </p>
            <p>RSC Imports continuously reviews new manufacturers, producers, brands and product categories.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-xs uppercase tracking-[0.25em] text-[#BFA26F] font-semibold">
            Supplier Information Requested
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6" data-testid="partner-provide-list">
            {PROVIDE_LIST.map((item) => (
              <li
                key={item}
                className="py-2.5 border-b border-[#FBF9F6]/10 text-sm font-light text-[#FBF9F6]/80 flex items-start gap-3"
              >
                <span className="mt-2 h-1 w-3 bg-[#BFA26F] shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="lg:col-span-7">
        <EnquiryForm />
      </Reveal>
    </div>
  </section>
);

export const CtaBand = ({ title, text, buttonLabel, to }) => (
  <section data-testid="cta-band" className="bg-[#0D2B1E] border-y border-[#BFA26F]/25">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20 flex flex-col md:flex-row md:items-center gap-8 md:gap-16">
      <div className="flex-1">
        <h2 className="font-display text-3xl md:text-4xl font-medium text-[#FBF9F6]">{title}</h2>
        {text && <p className="mt-4 text-sm md:text-base font-light text-[#FBF9F6]/70 max-w-xl leading-relaxed">{text}</p>}
      </div>
      <Link
        to={to}
        data-testid="cta-band-button"
        className="group inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#BFA26F] text-[#0A1F16] text-sm font-bold tracking-widest uppercase hover:bg-[#FBF9F6] transition-colors duration-300 w-full md:w-auto md:shrink-0"
      >
        {buttonLabel}
        <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
      </Link>
    </div>
  </section>
);
